/**
 * taskListColumns.tsx — nadpisania kolumn rdzennych listy zadań (`CoreColumnOverride`).
 *
 * Generyk `buildCoreColumns` nie umie narysować czterech grup pól zadania:
 *  - status i priorytet — listy wyboru o STAŁYCH wartościach z kodu (CHECK w bazie + enum frontu),
 *    nie ze słownika katalogu pól; komórka to kolorowa plakietka, filtr ma kolory dla Kanbana;
 *  - typ zadania, kontrahent i „zlecone przez" — odwołania BEZ celu w katalogu pól (typ to tabela
 *    `task_types`, nie kartoteka z adaptera): serwer zwraca ich TEKSTOWE etykiety w wierszu
 *    (`taskTypeName`, `contractorName`, `createdByName` — joiny deskryptora), więc sort i filtr
 *    nagłówka działają po tekście.
 * Osoba odpowiedzialna idzie generykiem: serwer dokłada jej etykietę do `extRefLabels`.
 */

import { TASK_PRIORITIES, TASK_STATUSES } from '@saas/shared';

import { Badge } from '@/components/Badge';
import type { ColumnDefinition } from '@/components/configurable-table';
import { PROCESS_COLUMN_ID_TO_SLUG } from '@/lib/documents/process-columns';
import { type CoreColumnOverride, isFieldFilterable } from '@/lib/ext-list-columns';
import type { TaskListItem } from '@/lib/tasks/task-api';
import {
  PRIORITY_TONE,
  priorityLabel,
  STATUS_KANBAN_COLOR,
  STATUS_TONE,
  statusLabel,
} from '@/pages/tasks/task-labels';
import { isTaskPriority, isTaskStatus } from '@/pages/tasks/task-model';

const EMPTY = <span className="text-text-tertiary">—</span>;

/** Komórka tekstowa z etykietą z joina — pusta wartość to myślnik. */
function labelCell(value: unknown): React.ReactNode {
  return value == null || value === '' ? EMPTY : <span>{String(value)}</span>;
}

/**
 * Nadpisanie kolumny tekstowej z etykietą z joina deskryptora (typ / kontrahent / autor):
 * `accessor` wskazuje klucz z etykietą, filtr nagłówka jest warunkowy po tekście (`conditionOnly`).
 */
function joinedLabelOverride(accessor: string, width: number): CoreColumnOverride<TaskListItem> {
  return ({ field, resolveSortable, resolveFilterable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: width,
    sortable: resolveSortable?.(field) ?? false,
    filterable: false,
    ...(isFieldFilterable(field, resolveFilterable)
      ? { headerFilter: { category: 'text' as const, fieldSlug: field.slug, conditionOnly: true } }
      : {}),
    renderCell: labelCell,
  });
}

/**
 * Rejestr nadpisań kolumn rdzennych zadań — STATYCZNY (tłumaczenia bierze z `ctx.t`), więc ten sam
 * obiekt obsługuje stronę listy i sekcję zadań w kokpicie decyzji (`section-kits.tsx`).
 */
export const TASK_CORE_OVERRIDES: Readonly<Record<string, CoreColumnOverride<TaskListItem>>> = {
  status: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 140,
    sortable: resolveSortable?.(field) ?? false,
    filterable: false,
    ...(isFieldFilterable(field, resolveFilterable)
      ? {
          headerFilter: {
            category: 'select' as const,
            fieldSlug: field.slug,
            fieldType: 'select',
            options: TASK_STATUSES.map((s) => ({
              value: s,
              label: statusLabel(t, s),
              color: STATUS_KANBAN_COLOR[s],
            })),
          },
        }
      : {}),
    renderCell: (value) =>
      typeof value === 'string' && isTaskStatus(value) ? (
        <Badge tone={STATUS_TONE[value]}>{statusLabel(t, value)}</Badge>
      ) : (
        EMPTY
      ),
  }),
  priority: ({ field, accessor, t, resolveSortable, resolveFilterable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 130,
    sortable: resolveSortable?.(field) ?? false,
    filterable: false,
    ...(isFieldFilterable(field, resolveFilterable)
      ? {
          headerFilter: {
            category: 'select' as const,
            fieldSlug: field.slug,
            fieldType: 'select',
            kanbanFallback: false,
            options: TASK_PRIORITIES.map((p) => ({ value: p, label: priorityLabel(t, p) })),
          },
        }
      : {}),
    renderCell: (value) =>
      typeof value === 'string' && isTaskPriority(value) ? (
        <Badge tone={PRIORITY_TONE[value]}>{priorityLabel(t, value)}</Badge>
      ) : (
        EMPTY
      ),
  }),
  task_type_id: ({ field, resolveSortable, resolveFilterable }) => ({
    id: field.slug,
    accessor: 'taskTypeName',
    header: field.name,
    defaultVisible: true,
    defaultWidth: 140,
    sortable: resolveSortable?.(field) ?? false,
    filterable: false,
    ...(isFieldFilterable(field, resolveFilterable)
      ? {
          headerFilter: {
            category: 'text' as const,
            fieldSlug: field.slug,
            conditionOnly: true,
          },
        }
      : {}),
    renderCell: (value) =>
      value == null || value === '' ? EMPTY : <Badge tone="accent">{String(value)}</Badge>,
  }),
  contractor_id: joinedLabelOverride('contractorName', 220),
  created_by_user_id: joinedLabelOverride('createdByName', 190),
};

/**
 * Kolejność kolumn rdzennych na liście — jawna (numer, tytuł, typ… ), niezależna od `sortOrder`
 * katalogu. Rdzenne pola spoza tej listy (nowe z katalogu) trafiają za nią, przed firmą i kategorią.
 */
export const TASK_KNOWN_CORE_ORDER: readonly string[] = [
  'system_number',
  'title',
  'task_type_id',
  'status',
  'priority',
  'due_at',
  'owner_user_id',
  'contractor_id',
];

/**
 * Układa kolumny rdzenne zadań w jawnej kolejności (`TASK_KNOWN_CORE_ORDER`), pozostałe rdzenne
 * (nowe z katalogu) za nimi. Jedna kolejność dla strony listy i sekcji kokpitu decyzji.
 */
export function orderTaskCoreColumns(
  core: ColumnDefinition<TaskListItem>[],
): ColumnDefinition<TaskListItem>[] {
  const coreById = new Map(core.map((c) => [c.id, c] as const));
  const known = TASK_KNOWN_CORE_ORDER.map((id) => coreById.get(id)).filter(
    (c): c is ColumnDefinition<TaskListItem> => c !== undefined,
  );
  const leftover = core.filter((c) => !TASK_KNOWN_CORE_ORDER.includes(c.id));
  return [...known, ...leftover];
}

/** Slugi obsługiwane osobnym builderem (firma) — pomijane przez katalog, żeby nie dublować kolumny. */
export const TASK_EXCLUDE_SLUGS: ReadonlySet<string> = new Set(['company_id']);

/**
 * Mapa id kolumny → slug backendu dla `?columns=` — część STAŁA: kolumny spoza `buildCoreColumns`
 * (firma ma id `company`, kategoria `category`) i kolumny przekrojowe procesu. Kolumny z katalogu
 * dopisuje wołający pętlą `id = slug`.
 */
export const TASK_COLUMN_ID_TO_SLUG: Readonly<Record<string, string>> = {
  company: 'company_id',
  category: 'category',
  ...PROCESS_COLUMN_ID_TO_SLUG,
};

/** Prefiks kluczy i18n kolumn procesu zadań (patrz `buildProcessColumns`). */
export const TASK_PROCESS_LABEL_PREFIX = 'tenant.tasks';
